import React, { Suspense, useContext, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { trace } from "firebase/performance";
import { perf } from "./firebase";
import "./styles/dark.scss";
import { SidebarStateProvider } from "./components/sidebar/SidebarStateProvider.js";
import { AuthContext, AuthContextProvider } from "./store/AuthContext";
import { getUserScope } from "./shared/utils/roleScope";
import { puedeAccederColaboracion } from "./pages/equiposColaborativos/colaboracionAccess";
import EmailDomainMask from "./shared/demo/EmailDomainMask";
import { LicenseInfo } from '@mui/x-license-pro';
import { clientesInputs, productInputs, userInputs } from "./formSource";

// Eager: login + Home. Home es la landing tras login, así evitamos un round-trip
// de chunk download entre login → /.
import Login from "./pages/login/Login";
import Home from "./pages/home/Home";

// Lazy: all other routes
const List = lazy(() => import("./pages/list/List"));
const ListClientes = lazy(() => import("./pages/list/ListClientes"));
const ListTiendasCliente = lazy(() => import("./pages/list/ListTiendasCliente"));
const ListContratosCliente = lazy(() => import("./pages/list/ListContratosCliente"));
const ListHistoricoServicios = lazy(() => import("./pages/list/ListHistoricoServicios"));
const ListHistoricoTrabajadores = lazy(() => import("./pages/list/ListHistoricoTrabajadores"));
const ListAccesos = lazy(() => import("./pages/list/ListAccesos"));
const Single = lazy(() => import("./pages/single/Single"));
const Details = lazy(() => import("./pages/details/Details"));
const Documents = lazy(() => import("./pages/documents/Documents"));
const Trash = lazy(() => import("./pages/documents/DeletedDocuments"));
const Servicio = lazy(() => import("./pages/servicio/Servicio.jsx"));
const UpdateServicio = lazy(() => import("./pages/servicio/UpdateServicio.jsx"));
const New = lazy(() => import("./pages/new/New"));
const Update = lazy(() => import("./pages/update/Update"));
const UpdateCliente = lazy(() => import("./pages/update/UpdateCliente"));
const NewCliente = lazy(() => import("./pages/new/NewCliente"));
const NewClienteTienda = lazy(() => import("./pages/new/NewClienteTienda"));
const ReactivarServicio = lazy(() => import("./pages/new/ReactivarServicio"));
const UpdateTienda = lazy(() => import("./pages/update/UpdateTienda"));
const BuzonAnonimo = lazy(() => import("./pages/buzonAnonimo/BuzonAnonimo.jsx"));
const PrivacyPolicy = lazy(() => import('./components/infoBox/PrivacyPolicy'));
const Forms = lazy(() => import("./pages/list/Forms"));
const FormsTable = lazy(() => import("./pages/list/FormsTable"));
const FormsUpdate = lazy(() => import("./pages/update/UpdateForm"));
const PdfDesigns = lazy(() => import("./pages/list/PdfDesigns"));
const PdfDesignEditor = lazy(() => import("./components/forms/PdfDesignEditor"));
const NotificationsForm = lazy(() => import("./pages/list/NotificationsForm"));
const ProfileContainer = lazy(() => import("./pages/user/ProfileContainer.js"));
const ConfigContainer = lazy(() => import("./pages/user/ConfigContainer.js"));
const EditAdminContainer = lazy(() => import("./pages/user/EditAdminContainer.js"));
const AddUserContainer = lazy(() => import("./pages/user/AddUserContainer.js"));
const DataCuadrantes = lazy(() => import("./pages/cuadrante/Cuadrante.jsx"));
const CuadranteServicio = lazy(() => import("./pages/cuadrante/CuadranteServicio.jsx"));
const CuadranteVigilante = lazy(() => import("./pages/cuadrante/CuadranteVigilantes.jsx"));
const CambiosTurno = lazy(() => import("./pages/cambiosTurno/CambiosTurno.jsx"));
const Solicitudes = lazy(() => import("./pages/solicitudes/Solicitudes.jsx"));
const EnviarCuadrantes = lazy(() => import("./pages/enviarCuadrantes/EnviarCuadrantes.jsx"));
const CompanyContainer = lazy(() => import("./pages/user/CompanyContainer.js"));
const Jornadas = lazy(() => import("./pages/list/Jornadas.jsx"));
const AddClientUser = lazy(() => import("./pages/user/AddUserClientContainer.js"));
const ClientPortalConfig = lazy(() => import("./pages/user/ClientPortalConfigContainer.js"));
const ControlRondas = lazy(() => import("./pages/controlRondas/ControlRondas"));
const DashboardNotifications = lazy(() => import("./pages/internalNotifications/DashboardNotifications.jsx"));
const ChatBot = lazy(() => import("./components/ChatBot/ChatBot.jsx"));
const DatasetsPage = lazy(() => import('./pages/datasets/DatasetsPage'));
const Storage = lazy(() => import('./pages/storage/Storage'));
const Logs = lazy(() => import('./pages/logs/Logs'));
const CalendarPage = lazy(() => import('./pages/calendar/Calendar'));
const Licenses = lazy(() => import('./pages/licenses/Licenses'));
const LogDetail = lazy(() => import('./pages/logs/LogDetail'));
const EquiposColaborativos = lazy(() => import('./pages/equiposColaborativos/EquiposColaborativos'));

// Set MUI license at module scope (not on every render)
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY || '');

/**
 * Componente de ruta protegida que requiere autenticación.
 * Redirige al usuario a la página de inicio de sesión si no está autenticado.
 *
 * IMPORTANTE: debe vivir a nivel de módulo, NO dentro de App. Definido dentro,
 * cada re-render de App (cambios de AuthContext durante el arranque) creaba un
 * TIPO de componente nuevo y React desmontaba/remontaba la página entera —
 * los efectos de carga de datos de cada página se ejecutaban 3 veces.
 *
 * @param {Object} children - Componente anidado dentro de RequireAuth.
 * @returns {JSX.Element} - Componente RequireAuth.
 */
const RequireAuth = ({ children, checkRole }) => {
  const { currentUser, adminData } = useContext(AuthContext);
  const tieneAccesoPorRol = () => {
    if (!checkRole || !adminData || !adminData.tipoUsuario) return true;
    return adminData.tipoUsuario.some(role => checkRole.includes(role));
  };

  return currentUser && tieneAccesoPorRol() ? children : <Navigate to="/login" />;
};

/**
 * Guard de la ruta "Colaboración en Equipos de Trabajo" (Kit Digital): acceso
 * para los 3 usuarios del allowlist (colaboracionAccess) Y para cualquier
 * administrador. Espeja la condición del enlace del Sidebar para que la URL
 * directa no la eluda.
 */
const RequireColaboracionEquipos = ({ children }) => {
  const { currentUser, adminData } = useContext(AuthContext);
  if (!currentUser) return <Navigate to="/login" />;
  const permitido =
    puedeAccederColaboracion(currentUser.email) || getUserScope(adminData).isAll;
  return permitido ? children : <Navigate to="/" />;
};

/**
 * Componente principal de la aplicación.
 */
function App() {
  const { currentService, adminData } = useContext(AuthContext);

  // Envía una traza al cargar la app para que Firebase Performance reciba datos y muestre el dashboard.
  // No-op si la telemetría está desactivada (perf === null).
  useEffect(() => {
    if (!perf) return;
    const t = trace(perf, "app_loaded");
    t.start();
    t.stop();
  }, []);

  // Prefetch deferido: el dashboard se renderiza primero; vigilantes/tiendas se
  // precargan cuando el navegador está idle para no bloquear el render inicial.
  useEffect(() => {
    if (!currentService?.id) return;
    const serviceId = currentService.id;
    const run = () => {
      import("./components/datatable/Datatable").then(mod => {
        mod.prefetchVigilantesData(serviceId, adminData);
      });
      import("./components/datatable/DatatableClientes").then(mod => {
        mod.prefetchClientesData(serviceId, adminData);
      });
    };
    let cancel;
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: 5000 });
      cancel = () => window.cancelIdleCallback(id);
    } else {
      const id = setTimeout(run, 800);
      cancel = () => clearTimeout(id);
    }
    return cancel;
  // adminData se vuelve a leer en cada idle callback; depender de él permite
  // que el prefetch se rehaga si el rol del usuario cambia (login distinto).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentService?.id, adminData]);
  return (
    <div className="app">
      <BrowserRouter>
        <AuthContextProvider>
          <SidebarStateProvider>
            {/* Máscara visual de dominios de correo (demo Kit Digital) */}
            <EmailDomainMask />
            <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Cargando...</div>}>
            <Routes>
              <Route path="/">
                <Route path="login" element={<Login />} />
                <Route path="/servicio" element={<Servicio />} />
                <Route path="/UpdateServicio" element={<UpdateServicio />} />
                <Route path="/company"
                  index
                  element={
                    <RequireAuth checkRole={['administrativo', 'administrador']}>
                      <CompanyContainer />
                    </RequireAuth>
                  }
                />
                <Route
                  index
                  element={
                    <RequireAuth>
                      <Home />
                    </RequireAuth>
                  }
                />
                <Route path="/profile"
                  index
                  element={
                    <RequireAuth>
                      <ProfileContainer />
                    </RequireAuth>
                  }
                />
                <Route path="/config"
                  index
                  element={
                    <RequireAuth>
                      <ConfigContainer />
                    </RequireAuth>
                  }
                />
                <Route path="/add-User"
                  index
                  element={
                    <RequireAuth>
                      <AddUserContainer />
                    </RequireAuth>
                  }
                />
                <Route path="/add-user-client/:clientId"
                  index
                  element={
                    <RequireAuth>
                      <AddClientUser />
                    </RequireAuth>
                  }
                />
                <Route path="/ClientPortalConfig"
                  index
                  element={
                    <RequireAuth checkRole={["administrador"]}>
                      <ClientPortalConfig />
                    </RequireAuth>
                  }
                />
                <Route path="/edit-admin/:adminId"
                  element={
                    <RequireAuth>
                      <EditAdminContainer />
                    </RequireAuth>
                  }
                />
                <Route path="vigilantes">
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <List />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="historico"
                    element={
                      <RequireAuth>
                        <ListHistoricoTrabajadores />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="historico/:id"
                    element={
                      <RequireAuth>
                        <Single soloLectura backTo="/vigilantes/historico" />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path=":id"
                    element={
                      <RequireAuth>
                        <Single />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="details/:id"
                    element={
                      <RequireAuth>
                        <Details />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="new"
                    element={
                      <RequireAuth>
                        <New inputs={userInputs} title="Añadir Usuario" />
                      </RequireAuth>
                    }
                  />
                  {/* Modificar esta ruta para usar :id en lugar de :tipVS */}
                  <Route
                    path="update/:id"
                    element={
                      <RequireAuth>
                        <Update inputs={userInputs} title="Actualizar Usuario" />
                      </RequireAuth>
                    }
                  />
                </Route>
                <Route path="control-rondas">
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <ControlRondas />
                      </RequireAuth>
                    }
                  />
                </Route>
                <Route path="cambios-turno">
                  <Route
                    index
                    element={
                      <RequireAuth checkRole={['administrador', 'inspector', 'administrativo']}>
                        <CambiosTurno />
                      </RequireAuth>
                    }
                  />
                </Route>
                <Route path="solicitudes">
                  <Route
                    index
                    element={
                      <RequireAuth checkRole={['administrador', 'inspector', 'administrativo']}>
                        <Solicitudes />
                      </RequireAuth>
                    }
                  />
                </Route>
                {/* Ruta para el buzón anónimo */}
                <Route path="buzon-anonimo">
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <BuzonAnonimo />
                      </RequireAuth>
                    }
                  />
                </Route>
                <Route path="notificaciones-internas">
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <DashboardNotifications />
                      </RequireAuth>
                    }
                  />
                </Route>
                <Route path="tiendas">
                  <Route
                    path="update/:tiendaId"
                    element={
                      <RequireAuth>
                        <UpdateTienda inputs={productInputs} title="Actualizar servicio" />
                      </RequireAuth>
                    }
                  />
                </Route>
                <Route path="clientes">
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <ListClientes />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/clientes/:clienteId/tiendas/:nombreCliente"
                    element={
                      <RequireAuth>
                        <ListTiendasCliente />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/clientes/:clienteId/contratos/:nombreCliente"
                    element={
                      <RequireAuth>
                        <ListContratosCliente />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="newCliente"
                    element={
                      <RequireAuth>
                        <NewCliente inputs={clientesInputs} title="Añadir nuevo cliente" />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/clientes/:clienteId/newClienteTienda/:nombreCliente"
                    element={
                      <RequireAuth>
                        <NewClienteTienda inputs={productInputs} title="Añadir nuevo servicio" />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="historicoServicios"
                    element={
                      <RequireAuth>
                        <ListHistoricoServicios />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="newServicio"
                    element={
                      <RequireAuth>
                        <NewClienteTienda inputs={productInputs} title="Crear servicio para cliente inactivo" selectCliente />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="reactivarServicio"
                    element={
                      <RequireAuth>
                        <ReactivarServicio />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="update/:clienteId"
                    element={
                      <RequireAuth>
                        <UpdateCliente inputs={clientesInputs} title="Actualizar cliente" />
                      </RequireAuth>
                    }
                  />
                </Route>
              </Route>
              <Route path="documentos">
                <Route
                  index
                  element={
                    <RequireAuth checkRole={['administrativo', 'administrador', 'inspector']}>
                      <Documents />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="papelera">
                <Route
                  index
                  element={
                    <RequireAuth checkRole={['administrativo', 'administrador', 'inspector']}>
                      <Trash />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="notificaciones">
                <Route
                  index
                  element={
                    <RequireAuth>
                      <NotificationsForm />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="form-table">
                <Route
                  index
                  element={
                    <RequireAuth>
                      <FormsTable />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="form-creator">
                <Route
                  index
                  element={
                    <RequireAuth>
                      <Forms />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="form-update/:id">
                <Route
                  index
                  element={
                    <RequireAuth>
                      <FormsUpdate />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="pdf-designs">
                <Route
                  index
                  element={
                    <RequireAuth>
                      <PdfDesigns />
                    </RequireAuth>
                  }
                />
                <Route
                  path="new"
                  element={
                    <RequireAuth>
                      <PdfDesignEditor />
                    </RequireAuth>
                  }
                />
                <Route
                  path="edit/:id"
                  element={
                    <RequireAuth>
                      <PdfDesignEditor />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="cuadrantes">
                <Route
                  index
                  element={
                    <RequireAuth>
                      <DataCuadrantes />
                    </RequireAuth>
                  }
                />

                <Route
                  path="edit-servicio/:id"
                  index
                  element={
                    <RequireAuth>
                      <CuadranteServicio />
                    </RequireAuth>
                  }
                />
                <Route
                  path="edit-vigilante/:id"
                  index
                  element={
                    <RequireAuth>
                      <CuadranteVigilante />
                    </RequireAuth>
                  }
                />
                <Route
                  path="enviar"
                  element={
                    <RequireAuth>
                      <EnviarCuadrantes />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route path="jornadas">
                <Route
                  index
                  element={
                    <RequireAuth>
                      <Jornadas />
                    </RequireAuth>
                  }
                />
                <Route
                  path=":vigilanteId"
                  element={
                    <RequireAuth>
                      <Jornadas />
                    </RequireAuth>
                  }
                />
              </Route>
              {/* Ruta para Colaboración en Equipos de Trabajo - Kit Digital (PRACON ANDALUCIA) */}
              <Route path="/colaboracion-equipos" element={
                <RequireColaboracionEquipos>
                  <EquiposColaborativos />
                </RequireColaboracionEquipos>
              } />
              <Route path="/datasets" element={
                <RequireAuth>
                  <DatasetsPage />
                </RequireAuth>
              } />
              <Route path="/storage" element={
                <RequireAuth>
                  <Storage />
                </RequireAuth>
              } />
              <Route path="/calendar" element={
                <RequireAuth>
                  <CalendarPage />
                </RequireAuth>
              } />
              <Route path="/licenses" element={
                <RequireAuth checkRole={['administrativo', 'administrador']}>
                  <Licenses />
                </RequireAuth>
              } />
              {/* Accesos de usuarios (historial de logins) */}
              <Route path="/accesos" element={
                <RequireAuth checkRole={['administrador', 'inspector', 'administrativo']}>
                  <ListAccesos />
                </RequireAuth>
              } />
              {/* Rutas públicas para logs - sin autenticación requerida pero con sidebar */}
              <Route path="/logs" element={<Logs />} />
            </Routes>
          </Suspense>
          </SidebarStateProvider>
        </AuthContextProvider>
        <Suspense fallback={<div>Cargando...</div>}>
        <Routes>
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          {/* Rutas públicas para logs individuales - sin autenticación ni sidebar */}
          <Route path="/logs/:company/:logId" element={<LogDetail />} />
        </Routes>
        </Suspense>

        {/* ChatBot flotante - aparece en todas las pantallas */}
        <Suspense fallback={null}>
          <ChatBot />
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;